import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseDestroy } from 'nera-common/angular';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnInit,
    ViewEncapsulation,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslocoModule } from '@jsverse/transloco';
import { AbsoluteMediaUrlPipe } from 'app/shared/pipes/absolute-media-url.pipe';
import { isSystemOrg } from 'app/core/constants/system-org.constants';
import { IOrganizationMembership } from 'app/core/models/tenant.model';
import { MyOrganizationsService } from 'app/core/services/my-organizations.service';
import { TenantContextService } from 'app/core/services/tenant-context.service';

/**
 * Org-picker — T3.3 (root-app-multi-tenant plan, Phase 3).
 *
 * Header dropdown for switching between the current user's organization
 * memberships (`GET /me/organizations`, `MyOrganizationsService`). Only
 * renders once the membership list resolves to *more than one* org — a user
 * with a single org has nothing to switch between, and rendering a
 * one-item/empty menu would just be chrome with no function. Mirrors the
 * `languages`/`user` header components in this same directory: icon-trigger
 * `mat-menu`, `takeUntilDestroyed` teardown, `OnPush`.
 *
 * Switching is a real navigation (`TenantContextService.switchToOrganization`),
 * not a client-side state flip — the *host* an org is served on is bound to
 * the hostname per approach.md D5/D8, so there is no in-place "switch" without
 * a page load.
 *
 * WHICH ORG IS "CURRENT" IS THE SESSION'S, NOT THE HOST'S. The org this
 * component names is `ITenantContext.actingOrgId` — the JWT `org_id` the
 * backend scopes every request to. It deliberately does NOT fall back to the
 * Origin-resolved `orgId` once the acting org is known: those two legitimately
 * differ on a root host (the root app's own hostname is a domain of the
 * platform org, so `orgId` reads as the platform org for *every* user), and
 * preferring it rendered "Default Organization" for a session that was really
 * acting in — and being served — another org, with the real org offered below
 * as a switch target. A picker that names an org the token is not in is worse
 * than one that names none.
 */
@Component({
    selector: 'org-picker',
    templateUrl: './org-picker.component.html',
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    exportAs: 'orgPicker',
    imports: [
        MatButtonModule,
        MatMenuModule,
        MatIconModule,
        TranslocoModule,
        AbsoluteMediaUrlPipe,
    ],
})
export class OrgPickerComponent extends BaseDestroy implements OnInit {
    private readonly _changeDetectorRef = inject(ChangeDetectorRef);
    private readonly _myOrganizationsService = inject(MyOrganizationsService);
    private readonly _tenantContextService = inject(TenantContextService);

    /**
     * Every membership, unfiltered. Naming the org the session is acting in
     * must work even when that org is not a *navigable* target in this
     * environment (in local dev, where `ROOT_DOMAIN` is `localhost`, any
     * `*.photoflow.vn` membership is a dead row). Navigability gates
     * clicking, never truth-telling.
     */
    private _allMemberships: IOrganizationMembership[] = [];

    /** Memberships this environment can actually navigate to — the menu's rows. */
    organizations: IOrganizationMembership[] = [];

    /**
     * Only worth offering the menu once there is somewhere ELSE to go — not
     * just "2+ navigable rows total". On a tenant host the current org is
     * usually itself one of the navigable rows, so "2+ total" and "1+ other
     * than current" agree. On the root host they can diverge: a membership
     * whose stored domain IS the bare production root (no label — e.g. the
     * pre-refactor "Default Organization"/Global org) is correctly excluded
     * from `organizations` (navigating there is a no-op, you're already
     * there), which permanently capped the old `length > 1` check at 1 for
     * that user no matter how many other real orgs they belonged to.
     */
    get hasMultipleOrgs(): boolean {
        return this.organizations.some((org) => !this.isCurrent(org));
    }

    /**
     * The membership the session is acting in. Read off the unfiltered list so
     * the header still names the right org when there is nowhere to switch to;
     * `null` only while the list or the session is still resolving, or when the
     * acting org is genuinely not among the user's memberships.
     */
    get currentOrganization(): IOrganizationMembership | null {
        return this._allMemberships.find((org) => this.isCurrent(org)) ?? null;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    ngOnInit(): void {
        this._myOrganizationsService
            .getMyOrganizations()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((organizations) => {
                this._allMemberships = organizations;
                // Drop rows that can never navigate: a full host outside this
                // env's root domain (e.g. a custom domain, or a prod host on
                // dev), or a subdomain-less membership that isn't the System
                // org (should never happen — the System org is the only
                // subdomain-less membership type now — but the filter stays
                // defensive: the root host they'd otherwise resolve to is the
                // System org's context, so a click would land somewhere else).
                this.organizations = organizations.filter((org) =>
                    this._tenantContextService.isNavigableOrgTarget(
                        org.orgSubdomain,
                        org.orgId
                    )
                );
                this._changeDetectorRef.markForCheck();
            });

        // The acting org arrives with the session (and again when
        // `me/permissions` hydrates), independently of the membership list —
        // re-render so the label isn't stuck on whatever was known first.
        this._tenantContextService.context$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => this._changeDetectorRef.markForCheck());
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Whether `org` is the organization the session is acting in.
     *
     * Precedence, strongest first:
     *
     * 1. `actingOrgId` — the token's `org_id`. Authoritative and final: once
     *    it is known, nothing weaker may answer, because the weaker signals
     *    describe the HOST, and on a root host the host's org is the platform
     *    org for every user regardless of their session.
     * 2. `orgId` — the Origin-resolved host org. Correct on a tenant host, and
     *    the best available answer before the session restores.
     * 3. the raw hostname slug, then "root host ⇒ platform org", so the menu
     *    doesn't briefly show *no* org as current on first paint.
     */
    isCurrent(org: IOrganizationMembership): boolean {
        const context = this._tenantContextService.context;
        if (context.actingOrgId) {
            return context.actingOrgId === org.orgId;
        }
        if (context.orgId) {
            return context.orgId === org.orgId;
        }
        if (context.hostnameSlug) {
            return this._slugOf(org.orgSubdomain) === context.hostnameSlug;
        }
        return context.mode === 'root' && isSystemOrg(org.orgId);
    }

    /** Navigate to `org`'s tenant host. No-op if it's already the current org. */
    switchTo(org: IOrganizationMembership): void {
        if (this.isCurrent(org)) {
            return;
        }
        this._tenantContextService.switchToOrganization(org.orgSubdomain, org.orgId);
    }

    trackByOrgId(index: number, org: IOrganizationMembership): string {
        return org.orgId ?? String(index);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Reduce `orgSubdomain` (bare label or full host — see tenant.model.ts's
     * `IOrganizationMembership.orgSubdomain` doc comment) to a bare label for
     * comparison against `TenantContextService`'s hostname-parsed slug.
     */
    private _slugOf(subdomainOrHost: string | null | undefined): string {
        const value = (subdomainOrHost ?? '').trim().toLowerCase();
        if (!value) {
            return '';
        }
        return value.includes('.') ? value.split('.')[0] : value;
    }
}
